import { useForm } from '@inertiajs/react';
import { Plus } from 'lucide-react';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { AdminSummary } from '@/types';

interface AddReviewerDialogProps {
  eventSlug: string;
  availableAdmins: AdminSummary[];
}

export function AddReviewerDialog({
  eventSlug,
  availableAdmins,
}: AddReviewerDialogProps) {
  const [open, setOpen] = useState(false);

  const form = useForm({ adminId: '' });
  const { data, setData, processing, reset } = form;
  const errors = form.errors as unknown as Record<string, string | undefined>;

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    form.post(`/admin/${eventSlug}/reviewers`, {
      preserveScroll: true,
      onSuccess: () => {
        setOpen(false);
        reset();
      },
    });
  };

  return (
    <>
      <Button
        onClick={() => setOpen(true)}
        disabled={availableAdmins.length === 0}
      >
        <Plus className="mr-2 h-4 w-4" />
        Add Reviewer
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Add Reviewer</DialogTitle>
            <DialogDescription>
              Pick an admin user to add to this event's reviewer pool.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid gap-2">
              <Label htmlFor="add-reviewer-admin">Admin User</Label>
              <Select
                value={data.adminId}
                onValueChange={(value) => setData('adminId', value)}
                disabled={processing}
              >
                <SelectTrigger id="add-reviewer-admin">
                  <SelectValue placeholder="Select an admin" />
                </SelectTrigger>
                <SelectContent>
                  {availableAdmins.map((admin) => (
                    <SelectItem key={admin.id} value={admin.id}>
                      {admin.fullName} ({admin.email})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.adminId && (
                <p className="text-destructive text-sm">{errors.adminId}</p>
              )}
            </div>

            {errors.base && (
              <div className="rounded-md border border-red-200 bg-red-50 p-4">
                <p className="text-red-600 text-sm">{errors.base}</p>
              </div>
            )}

            <div className="flex justify-end gap-2 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setOpen(false)}
                disabled={processing}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={processing}>
                {processing ? 'Adding...' : 'Add Reviewer'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
